import { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Route, Clock, Truck, ChevronRight, Package, ShieldCheck, Info } from "lucide-react";
import api from "../lib/api";
import AddressAutocomplete from "../components/AddressAutocomplete";

export default function IntercityLanding() {
  const location = useLocation();
  // Parse deux formats d'URL :
  //  - Nouveau : /demenagement-{origin}-vers-{destination}
  //  - Legacy  : /demenagement-paris-{destination}   (rétrocompat 15 URLs déjà indexées)
  let origin = "";
  let destination = "";
  const newMatch = location.pathname.match(/^\/demenagement-([a-z-]+?)-vers-([a-z-]+)\/?$/);
  const legacyMatch = location.pathname.match(/^\/demenagement-paris-([a-z-]+)\/?$/);
  if (newMatch) {
    origin = newMatch[1];
    destination = newMatch[2];
  } else if (legacyMatch) {
    origin = "paris";
    destination = legacyMatch[1];
  }
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [departure, setDeparture] = useState("");
  const [arrival, setArrival] = useState("");
  const [movingDate, setMovingDate] = useState("");

  useEffect(() => {
    if (!origin || !destination) { setNotFound(true); return; }
    api.get(`/intercity/${origin}-vers-${destination}`).then((r) => {
      const p = r.data;
      setPage(p);
      setDeparture(`${p.origin_name}, France`);
      setArrival(`${p.destination_name}, France`);
      document.title = p.title;
      const setMeta = (n, c) => {
        let el = document.querySelector(`meta[name="${n}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute("name", n); document.head.appendChild(el); }
        el.setAttribute("content", c);
      };
      setMeta("description", p.meta_description);
      setMeta("robots", "index,follow");
      let c = document.querySelector('link[rel="canonical"]');
      if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
      // Canonical toujours vers nouveau format (préserve autorité SEO)
      c.setAttribute("href", `${window.location.origin}/demenagement-${p.origin}-vers-${p.destination}`);
      injectSchema(p);
    }).catch(() => setNotFound(true));
  }, [origin, destination]);

  if (notFound) return (
    <div className="container mx-auto px-6 py-20 text-center">
      <h1 className="font-heading font-extrabold text-3xl mb-4 text-[#2C3E50]">Trajet non disponible</h1>
      <Link to="/" className="btn-primary">Retour à l'accueil</Link>
    </div>
  );
  if (!page) return <div className="container mx-auto px-6 py-20 text-center text-sm text-[#6B7280]">Chargement…</div>;

  const startQuote = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (departure) params.set("dep", departure);
    if (arrival) params.set("arr", arrival);
    if (movingDate) params.set("date", movingDate);
    navigate(`/devis${params.toString() ? "?" + params.toString() : ""}`);
  };

  return (
    <div className="animate-fade-up" data-testid={`intercity-page-${origin}-${destination}`}>
      {/* Breadcrumb */}
      <div className="container mx-auto px-6 lg:px-10 pt-8">
        <nav className="text-xs text-[#6B7280] flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#2BD391]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#2C3E50] font-semibold">{page.origin_name} → {page.destination_name}</span>
        </nav>
      </div>

      {/* HERO */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8FBF3] text-[#1AA66C] text-xs font-semibold mb-4">
                <Route className="w-3.5 h-3.5" /> Trajet inter-villes
              </div>
              <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#2C3E50] leading-tight mb-4" data-testid="intercity-h1">
                Déménagement<br/><span>{page.origin_name}</span> <span className="text-[#2BD391]">→ {page.destination_name}</span>
              </h1>
              <p className="text-lg text-[#6B7280] max-w-2xl mb-6">
                {page.distance_km} km · ~{page.duration_hours} h de route via {page.highways_prose || `l'${page.highways}`}. Comparez plusieurs devis auprès d'entreprises partenaires spécialisées dans ce trajet longue distance.
              </p>

              {/* Widget devis */}
              <form onSubmit={startQuote} className="bg-white border-2 border-[#E5E7EB] rounded-3xl p-4 md:p-5 shadow-sm max-w-2xl" data-testid="intercity-quote-widget">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <AddressAutocomplete value={departure} onChange={setDeparture} placeholder={`Adresse de départ (${page.origin_name})`} className="input-clean !pl-11" iconClassName="left-4" testid="intercity-departure" />
                  <AddressAutocomplete value={arrival} onChange={setArrival} placeholder={`Adresse d'arrivée (${page.destination_name})`} className="input-clean !pl-11" iconClassName="left-4" testid="intercity-arrival" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input type="date" value={movingDate} onChange={(e) => setMovingDate(e.target.value)} className="input-clean !text-sm md:col-span-2" data-testid="intercity-date" />
                  <button type="submit" className="btn-primary w-full !text-xs whitespace-nowrap" data-testid="intercity-cta">
                    Comparer les devis
                  </button>
                </div>
              </form>
            </div>

            {/* Stats trajet */}
            <aside className="lg:col-span-5">
              <div className="rounded-3xl border border-[#E5E7EB] bg-gradient-to-br from-[#F7F9FB] to-white p-6">
                <div className="eyebrow mb-4">Repères du trajet</div>
                <div className="space-y-4">
                  <StatRow icon={MapPin} label="Distance" value={`${page.distance_km} km`} />
                  <StatRow icon={Clock} label="Durée route" value={`≈ ${page.duration_hours} h`} />
                  <StatRow icon={Route} label="Itinéraire principal" value={page.highways_label || `Autoroute ${page.highways}`} />
                  <StatRow icon={Truck} label="Volume type" value="Studio → Maison" />
                </div>
              </div>

              {/* Prix indicatifs */}
              <div className="mt-4 rounded-3xl border border-[#E5E7EB] bg-white p-6">
                <div className="eyebrow mb-4">Prix indicatifs 2026</div>
                <ul className="divide-y divide-[#F0F2F5] text-sm">
                  <PriceRow label="Studio (15-20 m³)"    range={page.prices.studio} />
                  <PriceRow label="T2 (25-30 m³)"        range={page.prices.t2} />
                  <PriceRow label="T3 (35-40 m³)"        range={page.prices.t3} />
                  <PriceRow label="T4 / Maison (50+ m³)" range={page.prices.t4_maison} />
                </ul>
                <p className="text-[10px] text-[#8A98A5] mt-3 italic">Fourchettes indicatives, formule standard hors emballage complet et monte-meuble.</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* CORPS */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="space-y-6" data-testid="intercity-body">
            {(page.body_paragraphs || []).map((p, i) => (
              <p key={i} className="text-[#2C3E50] leading-relaxed text-[15px]">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-10 md:py-14 bg-[#F7F9FB]">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-3">Services adaptés au trajet</div>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#2C3E50] mb-8">Formules disponibles</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {page.services.map((s) => (
              <div key={s} className="bg-white border border-[#E5E7EB] rounded-2xl px-4 py-4 text-sm font-semibold text-[#2C3E50] text-center flex items-center justify-center gap-2">
                <Package className="w-4 h-4 text-[#2BD391]" /> {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Liens internes */}
      {page.internal_links?.length > 0 && (
        <section className="py-10 md:py-14 bg-white">
          <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
            <div className="eyebrow mb-3">Approfondir</div>
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-[#2C3E50] mb-6">À consulter également</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {page.internal_links.map((l) => (
                <Link key={l.to} to={l.to} className="group flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-[#F7F9FB] hover:bg-[#E8FBF3] border border-transparent hover:border-[#2BD391] transition-all">
                  <span className="font-semibold text-[#2C3E50]">{l.label}</span>
                  <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#2BD391] group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faq?.length > 0 && (
        <section className="py-14 md:py-20 bg-[#F7F9FB]">
          <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
            <div className="eyebrow mb-3">Questions fréquentes</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#2C3E50] mb-8">{page.origin_name} → {page.destination_name}</h2>
            <div className="space-y-3" data-testid="faq-list">
              {page.faq.map((item, i) => (
                <details key={i} className="group bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer p-5 flex items-start justify-between gap-4 font-semibold text-[#2C3E50] hover:bg-[#E8FBF3] transition-colors">
                    <span>{item.q}</span>
                    <ChevronRight className="w-4 h-4 shrink-0 mt-1 group-open:rotate-90 transition-transform text-[#2BD391]" />
                  </summary>
                  <div className="px-5 pb-5 text-sm text-[#5C6B7A] leading-relaxed">{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mention conformité */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="bg-[#F7F9FB] border border-[#E5E7EB] rounded-2xl p-5 text-xs text-[#6B7280] leading-relaxed">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-[#2BD391] shrink-0 mt-0.5" />
              <p>
                <strong className="text-[#2C3E50]">Déménageur Pro est une plateforme de mise en relation.</strong>{" "}
                Les demandes de devis pour le trajet {page.origin_name} → {page.destination_name} sont transmises uniquement à des entreprises
                partenaires ou expressément autorisées à les recevoir. Les prix affichés sont des fourchettes indicatives ; seul le devis d'un professionnel fait foi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="bg-gradient-to-br from-[#2BD391] to-[#1AA66C] rounded-3xl p-10 md:p-14 text-center">
            <h2 className="font-heading font-extrabold text-white text-3xl md:text-4xl mb-3" style={{ color: "white" }}>{page.origin_name} → {page.destination_name} : recevez vos devis</h2>
            <p className="text-white/90 max-w-xl mx-auto mb-7">Un formulaire, plusieurs devis d'entreprises partenaires spécialisées dans ce trajet.</p>
            <Link to="/devis" className="inline-flex items-center gap-2 bg-white text-[#1AA66C] hover:bg-[#1A2A3A] hover:text-white rounded-full px-8 py-4 font-bold text-sm uppercase tracking-wider transition-all">
              Comparer plusieurs devis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const StatRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-[#E5E7EB]">
      <Icon className="w-4 h-4 text-[#2BD391]" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">{label}</div>
      <div className="font-semibold text-[#2C3E50]">{value}</div>
    </div>
  </div>
);

const PriceRow = ({ label, range }) => (
  <li className="flex items-center justify-between py-2.5">
    <span className="text-[#2C3E50]">{label}</span>
    <span className="font-bold text-[#1AA66C]">{range[0].toLocaleString("fr-FR")} – {range[1].toLocaleString("fr-FR")} €</span>
  </li>
);

function injectSchema(page) {
  document.querySelectorAll('script[data-intercity-schema]').forEach((n) => n.remove());
  const url = `${window.location.origin}/demenagement-${page.origin}-vers-${page.destination}`;
  const graph = [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${window.location.origin}/` },
        { "@type": "ListItem", position: 2, name: `${page.origin_name} → ${page.destination_name}`, item: url },
      ],
    },
    {
      "@type": "Service",
      serviceType: "Déménagement longue distance",
      name: `Déménagement ${page.origin_name} → ${page.destination_name}`,
      provider: { "@type": "Organization", name: "Déménageur Pro", url: window.location.origin },
      areaServed: [
        { "@type": "City", name: page.origin_name },
        { "@type": "City", name: page.destination_name },
      ],
      description: page.meta_description,
      offers: { "@type": "Offer", price: page.prices.t2[0], priceCurrency: "EUR", description: `À partir de ${page.prices.t2[0]} € pour un T2` },
    },
  ];
  if (page.faq?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-intercity-schema", "1");
  s.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
  document.head.appendChild(s);
}
